import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import DocumentDetailModal from '../components/DocumentDetailModal';
import { generateBillOfSalePDF, generateInvoicePDF, downloadPDF } from '../utils/pdfGenerator';
import './Documents.css';

const Documents = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isPremium } = useAuth();
  
  // Get initial filter from URL params or location state
  const searchParams = new URLSearchParams(location.search);
  const initialType = searchParams.get('type') || location.state?.type || 'all';
  
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(initialType);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  const ITEMS_PER_PAGE = 10;

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      let allDocs = [];
      
      if (filter === 'all' || filter === 'bills') {
        const billsRes = await api.get(`/bills?limit=100`);
        const bills = (billsRes.data.bills || []).map(b => ({ ...b, docType: 'bill' }));
        allDocs = [...allDocs, ...bills];
      }
      
      if (filter === 'all' || filter === 'invoices') {
        const invoicesRes = await api.get(`/invoices?limit=100`);
        const invoices = (invoicesRes.data.invoices || []).map(i => ({ ...i, docType: 'invoice' }));
        allDocs = [...allDocs, ...invoices];
      }
      
      // Sort by date (newest first)
      allDocs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      // Filter by search query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        allDocs = allDocs.filter(doc => {
          const searchFields = [
            doc.docType === 'bill' ? doc.documentNumber : doc.invoiceNumber,
            doc.docType === 'bill' ? doc.item?.description : doc.to?.name,
            doc.docType === 'bill' ? doc.seller?.name : doc.from?.name,
            doc.docType === 'bill' ? doc.buyer?.name : doc.to?.name,
          ].filter(Boolean).map(s => s.toLowerCase());
          
          return searchFields.some(field => field.includes(query));
        });
      }
      
      // Pagination
      setTotalDocs(allDocs.length);
      setTotalPages(Math.ceil(allDocs.length / ITEMS_PER_PAGE));
      
      const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
      const paginatedDocs = allDocs.slice(startIndex, startIndex + ITEMS_PER_PAGE);
      
      setDocuments(paginatedDocs);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Error loading documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isPremium()) {
      navigate('/dashboard');
      toast.info('Upgrade to Premium to access document history');
      return;
    }
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, currentPage, isPremium, navigate]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchDocuments();
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1);
    // Update URL
    navigate(`/documents${newFilter !== 'all' ? `?type=${newFilter}` : ''}`, { replace: true });
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const handleViewDocument = (doc) => {
    setSelectedDocument(doc);
    setSelectedType(doc.docType);
  };

  const handleCloseModal = () => {
    setSelectedDocument(null);
    setSelectedType(null);
  };

  const handleDownloadPDF = (doc) => {
    try {
      if (doc.docType === 'bill') {
        const pdf = generateBillOfSalePDF(doc);
        downloadPDF(pdf, `bill-of-sale-${doc.documentNumber}.pdf`);
      } else {
        const pdf = generateInvoicePDF(doc);
        downloadPDF(pdf, `invoice-${doc.invoiceNumber}.pdf`);
      }
      toast.success('PDF downloaded!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error generating PDF');
    }
  };

  const handleResendEmail = async (doc) => {
    setActionLoading(true);
    try {
      const endpoint = doc.docType === 'bill' 
        ? `/bills/${doc._id}/resend-email` 
        : `/invoices/${doc._id}/resend-email`;
      await api.post(endpoint);
      toast.success('Email sent successfully!');
    } catch (error) {
      console.error('Error resending email:', error);
      toast.error('Error sending email');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteDocument = async (doc) => {
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }

    setActionLoading(true);
    try {
      const endpoint = doc.docType === 'bill' ? `/bills/${doc._id}` : `/invoices/${doc._id}`;
      await api.delete(endpoint);
      
      setDocuments(prev => prev.filter(d => d._id !== doc._id));
      setTotalDocs(prev => prev - 1);
      handleCloseModal();
      toast.success('Document deleted');
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Error deleting document');
    } finally {
      setActionLoading(false);
    }
  };

  const getDocumentTitle = (doc) => {
    if (doc.docType === 'bill') {
      return doc.item?.description || 'Bill of Sale';
    }
    return doc.to?.name || 'Invoice';
  };

  const getDocumentNumber = (doc) => {
    return doc.docType === 'bill' ? doc.documentNumber : doc.invoiceNumber;
  };

  const getDocumentAmount = (doc) => {
    return doc.docType === 'bill' ? doc.transaction?.salePrice : doc.total;
  };

  return (
    <div className="documents-page">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">My Documents</h1>
          <p className="page-subtitle">
            View and manage all your saved bills of sale and invoices
          </p>
        </div>

        {/* Filters and Search */}
        <div className="documents-toolbar">
          <div className="filter-tabs">
            <button 
              className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
              onClick={() => handleFilterChange('all')}
            >
              All
            </button>
            <button 
              className={`filter-tab ${filter === 'bills' ? 'active' : ''}`}
              onClick={() => handleFilterChange('bills')}
            >
              📝 Bills of Sale
            </button>
            <button 
              className={`filter-tab ${filter === 'invoices' ? 'active' : ''}`}
              onClick={() => handleFilterChange('invoices')}
            >
              💰 Invoices
            </button>
          </div>
          
          <form onSubmit={handleSearch} className="search-form">
            <input
              type="text"
              className="search-input"
              placeholder="Search by name, item, or document #..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit" className="btn btn-primary btn-sm">
              Search
            </button>
          </form>
        </div>

        {/* Results Summary */}
        <div className="results-summary">
          {totalDocs} document{totalDocs !== 1 ? 's' : ''} found
        </div>

        {/* Documents List */}
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : documents.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📁</div>
            <h3>No documents found</h3>
            <p>
              {searchQuery 
                ? 'Try adjusting your search terms'
                : 'Start creating bills of sale and invoices to see them here.'
              }
            </p>
          </div>
        ) : (
          <>
            <div className="documents-list">
              {documents.map(doc => (
                <div key={doc._id} className="document-item">
                  <div className="document-type-badge">
                    {doc.docType === 'bill' ? '📝' : '💰'}
                  </div>
                  <div className="document-info">
                    <h4>{getDocumentTitle(doc)}</h4>
                    <p>
                      {getDocumentNumber(doc)} • {formatDate(doc.createdAt)}
                    </p>
                  </div>
                  <div className="document-amount">
                    {formatCurrency(getDocumentAmount(doc))}
                  </div>
                  <span className={`badge badge-${
                    doc.docType === 'bill' 
                      ? (doc.sellerSignature || doc.buyerSignature ? 'success' : 'secondary')
                      : (doc.status === 'paid' ? 'success' : doc.status === 'overdue' ? 'danger' : 'secondary')
                  }`}>
                    {doc.docType === 'bill' 
                      ? (doc.sellerSignature || doc.buyerSignature ? 'Signed' : 'Unsigned')
                      : doc.status
                    }
                  </span>
                  <div className="document-actions">
                    <button 
                      className="action-btn" 
                      onClick={() => handleViewDocument(doc)}
                      title="View Details"
                    >
                      👁️
                    </button>
                    <button 
                      className="action-btn" 
                      onClick={() => handleDownloadPDF(doc)}
                      title="Download PDF"
                    >
                      📥
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button 
                  className="pagination-btn"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  ← Previous
                </button>
                <span className="pagination-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button 
                  className="pagination-btn"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Document Detail Modal */}
      {selectedDocument && (
        <DocumentDetailModal
          document={selectedDocument}
          type={selectedType}
          onClose={handleCloseModal}
          onDownload={() => handleDownloadPDF(selectedDocument)}
          onResendEmail={() => handleResendEmail(selectedDocument)}
          onDelete={() => handleDeleteDocument(selectedDocument)}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

export default Documents;
